/*
    Copyright (c) Computer-L.A.N. GmbH (http://www.computer-lan.de).
    Alle Rechte vorbehalten.
*/

import { Button, Container, styled, Typography } from "@mui/material";
import { ArrowRightIcon, CheckIcon, ClockIcon } from "../../../../Basis/FontAwesome";
import { nameof } from "../../../../Basis/Utils/AnyUtil";
import { translationKeys, useTranslation } from "../../Internationalisierung/i18nEinstellungen";
import { SCHRITTE } from "../../Queries/Schnelleinstieg";
import ActionsBar from "./ActionsBar";
import { useSchnelleinstiegContext } from "./SchnelleinstiegContext";

const SchrittList = styled("ul")(({ theme }) => ({
    display: "block",
    listStyle: "none",
    padding: 0,
    margin: 0,
    marginBlock: theme.spacing(3),
}));

const SchrittListItem = styled("li")(({ theme }) => ({
    display: "flex",
    gap: theme.spacing(),
    paddingBlockEnd: theme.spacing(2),
}));

type SchrittAvatarProps = {
    istDurchgefuehrt?: boolean;
    istNaechsterSchritt?: boolean;
}

const SchrittAvatar = styled("div", {
    shouldForwardProp: prop => (
        prop !== nameof<SchrittAvatarProps>("istDurchgefuehrt")
        && prop !== nameof<SchrittAvatarProps>("istNaechsterSchritt")
    ),
})<SchrittAvatarProps>(({ theme, istDurchgefuehrt, istNaechsterSchritt }) => ([
    {
        width: "2rem",
        height: "2rem",
        flexShrink: 0,
        fontSize: "1.1rem",
        lineHeight: "2rem",
        textAlign: "center",
        borderRadius: "50%",
        backgroundColor: theme.palette.primary.main,
        color: theme.palette.primary.contrastText,
    },
    !!istDurchgefuehrt && {
        display: "grid",
        placeItems: "center",
        backgroundColor: theme.palette.success.dark,
        color: theme.palette.getContrastText(theme.palette.success.dark),
    },
    (!istDurchgefuehrt && !istNaechsterSchritt) && {
        backgroundColor: `rgba(0, 0, 0, 0.38)`,
    },
]));

export default function SchnelleinstiegStart() {
    const { naechsterSchritt, istDurchgefuehrt, start, ueberspringen } = useSchnelleinstiegContext();

    const { t } = useTranslation()

    return (
        <Container
            maxWidth="sm"
            sx={{ m: "auto", py: 2 }}
        >
            <Typography variant="h4" align="center" paragraph>
                {t(translationKeys.schnelleinstiegStart.titel)}
            </Typography>

            <Typography variant="subtitle1" paragraph >
                <span
                    dangerouslySetInnerHTML={{
                        __html: t(translationKeys.schnelleinstiegStart.text)
                    }}
                />
            </Typography>

            <SchrittList>
                {SCHRITTE.map(schritt => (
                    <SchrittListItem key={schritt}>
                        <SchrittAvatar
                            istDurchgefuehrt={istDurchgefuehrt(schritt)}
                            istNaechsterSchritt={schritt === naechsterSchritt}
                        >
                            {istDurchgefuehrt(schritt) ? <CheckIcon /> : schritt + 1}
                        </SchrittAvatar>
                        <div>
                            <Typography
                                sx={{ lineHeight: "2rem" }}
                                dangerouslySetInnerHTML={{
                                    __html: t(translationKeys.schnelleinstiegStart.schritt.titel[schritt])
                                }}
                            />
                            <Typography>
                                {t(translationKeys.schnelleinstiegStart.schritt.text[schritt])}
                            </Typography>
                        </div>
                    </SchrittListItem>
                ))}
            </SchrittList>

            <ActionsBar>
                <Button
                    onClick={ueberspringen}
                    variant="text"
                    color="secondary"
                    endIcon={<ClockIcon />}
                >
                    {t(translationKeys.schnelleinstiegStart.ueberspringen)}
                </Button>
                <Button
                    onClick={start}
                    variant="contained"
                    color="primary"
                    endIcon={<ArrowRightIcon />}
                >
                    {t(translationKeys.schnelleinstiegStart.losGehts)}
                </Button>
            </ActionsBar>
        </Container>
    )
}